import Button from "components/buttons";
import Divider from "components/divider";
import DynamicFormInput from "components/form/form";
import FileUpload from "components/FileUpload";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";

import {
  createLegalResearchRequest,
  createLegalResearchRequestCommittee,
  updateLegalResearchRequest,
  updateLegalResearchRequestCommittee,
} from "services/legalResearchService";
import {
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_TYPES,
} from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { jwtDecode } from "jwt-decode";

interface TokenPayload {
  sub: string;
  exp: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  organization?: any;
}

const CreateNewCommitteeRequest = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const contextData = useAuth();

  const location = useLocation();
  const editData = location.state?.request;

  const [formState, setFormState] = useState({
    date: editData?.date || "",
    letter_reference_number: editData?.letter_reference_number || "",
    request_summary: editData?.request_summary || "",
    request_letter_file: null as File | null,
    committee_request_file: null as File | null,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
  const fileInputRefDraftInstruction = useRef<HTMLInputElement>(null);

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (
    file: any,
    fieldName: keyof Pick<
      typeof formState,
      "request_letter_file" | "committee_request_file"
    >,
  ) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: file,
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formState.date) newErrors.date = t("DateRequired");
    if (!formState.letter_reference_number)
      newErrors.letter_reference_number = t("LetterReferenceNumberRequired");
    if (!formState.request_summary)
      newErrors.request_summary = t("ResearchCommitteeSupportSummaryRequired");
    if (!formState.request_letter_file)
      newErrors.request_letter_file = t("RequestLetterRequired");
    if (!formState.committee_request_file)
      newErrors.committee_request_file = t("CommitteeRequestFileRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast(t("PleaseFillAllRequiredFields"), "error");
      return;
    }
    setSubmitting(true);

    const data = new FormData();
    for (const key in formState) {
      const value = formState[key as keyof typeof formState];
      if (value !== null) {
        if (value instanceof File) {
          data.append(key, value);
        } else {
          data.append(key, String(value));
        }
      }
    }
    if (editData) {
      try {
        const response = await updateLegalResearchRequestCommittee(
          editData.id,
          data,
          contextData.token || contextData.user.access_token || "",
        );
        if (response.id) {
          console.log("preparing for navigation");
          navigate(`/conduct-legal-research/${response.id}`);
        }
        showToast(t("LegalResearchRequestSentSuccess"), "success");
        setErrors({});
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t("FailedToSendLegalResearchRequest"), "error");
      } finally {
        setSubmitting(false);
      }
    } else {
      const decoded = jwtDecode<TokenPayload>(
        contextData.user.access_token || "",
      );
      console.log("decode", decoded);
      data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.pending.value);
      data.append("requested_by", contextData.user?.user_type);
      data.append("requested_by_id", contextData.user?.uu_id ?? "");
      data.append("stackholder_id", contextData?.user?.org_unit_id ?? "");
      data.append("first_name", decoded?.first_name || "");
      data.append("last_name", decoded?.last_name || "");
      data.append("email_user_organization", decoded?.email || "");
      data.append(
        "organization_kebele",
        decoded?.organization?.address?.kebele || "",
      );
      data.append(
        "organization_zone",
        decoded?.organization?.address?.zone || "",
      );
      data.append(
        "organization_state",
        decoded?.organization?.address?.state || "",
      );
      data.append("organization_name", decoded?.organization?.name || "");
      data.append("organization_type", decoded?.organization?.type || "");

      try {
        const response = await createLegalResearchRequestCommittee(
          contextData.token || contextData.user.access_token || "",
          data,
        );
        if (response.id) {
          console.log("preparing for navigation");
          navigate(`/conduct-legal-research/committee/${response.id}`);
        }
        showToast(t("LegalResearchRequestSentSuccess"), "success");
        setErrors({});
        setFormState({
          date: "",
          letter_reference_number: "",
          request_summary: "",
          request_letter_file: null,
          committee_request_file: null,
        });
        if (fileInputRefRequestLetter.current)
          fileInputRefRequestLetter.current.value = "";
        if (fileInputRefDraftInstruction.current)
          fileInputRefDraftInstruction.current.value = "";
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t("FailedToSendLegalResearchRequest"), "error");
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <div>
      <PageTitle
        icon="⚖"
        text={t("RequestForConductingLegalResearch")}
        subtitle={t("LegalResearchSubtitle")}
        iconColor="text-purple-600"
        textColor="text-[#432101]"
      />
      <form onSubmit={handleSubmit}>
        <TwoColumnLayout
          left={
            <div className="py-[22px] px-[24px] bg-white">
              <Divider
                orientation="horizontal"
                text={t("ResearchDetail")}
                textPosition="left"
                textClassName="text-[#9D0303] font-[600] text-[18px]"
                className="my-4"
              />
              <div className="grid gap-6 mb-6 md:grid-cols-2 ">
                <DynamicFormInput
                  inputs={[
                    {
                      value: formState.letter_reference_number,
                      type: "number",
                      label: t("LetterReferenceNumberRequired"),
                      name: "letter_reference_number",
                      onChange: handleChange,
                      placeholder: t("LetterReferenceNumberPlaceholder"),
                      error: errors.letter_reference_number,
                    },
                  ]}
                />
                <div>
                  <input
                    type="date"
                    id="schedule_datetime"
                    name="date"
                    className="w-full bg-gray-50 p-3 bg-[#f2f2f2] mt-auto h-10 border-gray-300 rounded-md pr-10 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                    value={formState?.date}
                    onChange={handleChange}
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                  {errors.date && (
                    <span className="text-red-500 text-sm">{errors.date}</span>
                  )}
                </div>
              </div>
              <DynamicFormInput
                inputs={[
                  {
                    value: formState.request_summary,
                    type: "textarea",
                    label: t("ResearchCommitteeSupportSummaryRequired"),
                    name: "request_summary",
                    onChange: handleChange,
                    placeholder: t("DetailHere"),
                    style: {
                      height: "15rem",
                    },
                    error: errors.request_summary,
                  },
                ]}
              />
              <div className="flex flex-col md:flex-row gap-6 my-4">
                <div className="flex-1">
                  <FileUpload
                    label={t("RequestLetterRequired")}
                    onChange={(file) =>
                      handleFileChange(file, "request_letter_file")
                    }
                    ref={fileInputRefRequestLetter}
                  />
                  {errors.request_letter_file && (
                    <span className="text-red-500 text-sm">
                      {errors.request_letter_file}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <FileUpload
                    label={t("CommitteeRequestFileRequired")}
                    onChange={(file) =>
                      handleFileChange(file, "committee_request_file")
                    }
                    ref={fileInputRefDraftInstruction}
                  />
                  {errors.committee_request_file && (
                    <span className="text-red-500 text-sm">
                      {errors.committee_request_file}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex space-x-4 justify-between">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFormState({
                      date: "",
                      letter_reference_number: "",
                      request_summary: "",
                      request_letter_file: null,
                      committee_request_file: null,
                    });
                    setErrors({});
                    if (fileInputRefRequestLetter.current)
                      fileInputRefRequestLetter.current.value = "";
                    if (fileInputRefDraftInstruction.current)
                      fileInputRefDraftInstruction.current.value = "";
                  }}
                >
                  {t("Cancel")}
                </Button>
                <Button type="submit" variant="primary">
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon} {t("Submitting")}
                    </div>
                  ) : (
                    t("Submit")
                  )}
                </Button>
              </div>
            </div>
          }
          right={
            <div className="w-full max-w-md space-y-4">
              <Notes
                title={t("Note")}
                text={t("ImportantNotesText")}
                subtext={t("ImportantNotesSubtext")}
              />
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="font-semibold">{t("DocumentRequirements")}</h2>
                    <p className="text-sm text-gray-500">
                      {t("DocumentRequirementsDescription")}
                    </p>
                  </div>
                </div>
                <div className="border-t pt-2 space-y-2">
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("ConductingLegalResearch")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">
                      {t("LegislativeDrafting")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </form>
    </div>
  );
};

export default CreateNewCommitteeRequest;